import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid";

export const PIPELINE_EMPTY_TEXT = 'Add a property to start analyzing and tracking deals.';
export const PIPELINE_PAGE_SIZES = [25, 50, 100];

// SOW 4 A1c: every table border is #C6CFC6, the unchecked box included.
const UncheckedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" fill="white" stroke="#C6CFC6"/>
  </svg>
)

// Only the header can be indeterminate; MUI draws its own blue square there unless this is passed
// (Comment Set #1, Pipeline 4). Values: `sheet-heading/Frame 176/codex:check`.
const IndeterminateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" fill="#E7F0E2" stroke="#4CBC18"/>
    <path d="M4 7H10" stroke="#4CBC18" strokeLinecap="round"/>
  </svg>
)

const CheckedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" fill="#F3FFEB" stroke="#4CBC18"/>
    <path d="M3.5 7L5.5 9.5L10.5 4.5" stroke="#4CBC18" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// `arrow-sorting_2`: 7x7, stroke 1 #4CBC18, round caps. MUI passes fontSize/className; only the
// class reaches the DOM.
export const SortArrowDown = ({ className }) => (
  <svg className={className} width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0.5 4L4 7.5L7.5 4M4 7.5V0.5" stroke="#4CBC18" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const SortArrowUp = ({ className }) => (
  <svg className={className} width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0.5 4L4 0.5L7.5 4M4 0.5V7.5" stroke="#4CBC18" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const EmptyOverlay = () => (
  <div className="flex h-full w-full items-center justify-center px-4 text-center text-[14px] font-normal text-brand-dark">
    {PIPELINE_EMPTY_TEXT}
  </div>
)

// The checkbox column is 42 wide with the box 16 from the table edge (spec.md a2).
const CHECKBOX_COLUMN = { ...GRID_CHECKBOX_SELECTION_COL_DEF, width: 42, minWidth: 42 }

const PipelineGrid = ({
  rows,
  columns,
  selectedRows,
  onRowSelectionChange,
  paginationModel,
  onPaginationModelChange,
}) => {
  return (
    <div className="w-full overflow-hidden rounded-[8px] bg-white border border-[#C6CFC6]">
      <DataGrid
        rows={rows}
        columns={[CHECKBOX_COLUMN, ...columns]}
        rowSelectionModel={{ type: 'include', ids: new Set(selectedRows) }}
        // Without this the header checkbox emits { type: 'exclude', ids: [] } ("all but none"), which
        // the include-only mapping below read as nothing selected (SOW 4 A1e). With it MUI emits the
        // real ids of every row passed in -- the whole active tab, all pages.
        disableRowSelectionExcludeModel
        onRowSelectionModelChange={(model) => {
            if (model?.type === 'include') {
                onRowSelectionChange([...model.ids]);
            } else {
                onRowSelectionChange([]);
            }
        }}
        checkboxSelection
        disableColumnMenu
        disableRowSelectionOnClick
        // Client-side pages; the bar itself is PipelinePagination under the table.
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={PIPELINE_PAGE_SIZES}
        hideFooter
        autoHeight
        columnHeaderHeight={48}
        rowHeight={72}
        slots={{
          noRowsOverlay: EmptyOverlay,
          columnSortedDescendingIcon: SortArrowDown,
          columnSortedAscendingIcon: SortArrowUp,
          columnUnsortedIcon: SortArrowDown,
        }}
        slotProps={{
          baseCheckbox: {
            icon: <UncheckedIcon />,
            checkedIcon: <CheckedIcon />,
            indeterminateIcon: <IndeterminateIcon />,
          },
        }}
        sx={{
          border: "none",
          fontFamily: 'Figtree',
          '--DataGrid-rowBorderColor': '#C6CFC6',
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#FFFFFF",
          },

          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: 14,
            fontWeight: 700,
            color: "#17270D",
          },

          // The drawn arrow sits 5.5 after "Added" at rest, not only on hover.
          "& .MuiDataGrid-iconButtonContainer": {
            visibility: "visible",
            width: "auto",
            marginLeft: "3.5px",
          },
          "& .MuiDataGrid-sortButton": {
            padding: 0,
            width: 8,
            height: 8,
            "&:hover": { backgroundColor: "transparent" },
          },

          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            outline: "none !important",
          },

          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },

          // .Mui-selected:hover below outranks this, so a ticked row keeps its colour under the pointer.
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#F7F9F6",
          },

          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "#DDE9D8",
          },

          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "#DDE9D8",
          },

          // Comment Set #2, 1.1.1: the box's edge goes #4CBC18 under the pointer.
          "& .MuiCheckbox-root": {
            padding: "5px",
            "&:hover": { backgroundColor: "transparent" },
            "&:hover rect": { stroke: "#4CBC18" },
          },

          // 11 + the checkbox's own 5px padding = the box 16 from the table edge.
          "& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
            justifyContent: "flex-start",
          },
          "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
            marginLeft: "11px",
          },

          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },

          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
            padding: '0 !important',
          },
        }}
      />
    </div>
  );
};

export default PipelineGrid;
